import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Header, Label, Modal, Segment } from 'semantic-ui-react';
import { ReservationPolicy, Reservations } from '../../api/services/next';
import { GetReservationTotals } from '../../api/services/pms';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import { useTextResources, useMessageResources } from '../../language';
import { onError } from '../shared/messages';
import { useLoader } from "../../hooks/useLoader";
import { RequiredGtdTypeEnum, GtdOverrideStatuses } from '../shared/constants';
import { convertUtcTimeToHotelLocal } from '../shared/timezoneUtils';
import CancellationPoliciesList from './CancellationPoliciesList';
import DepositPoliciesList from './DepositPoliciesList';
import './style.css';

const ReservationPolicyPopup = ({ onClose, controller, ...props }) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const currentHotel = useCurrentHotel();

    const [open, setOpen] = useState(props.open)
    const [reservation, setReservation] = useState()
    const [total, setTotal] = useState()
    const [policy, setPolicy] = useState()
    const [cancellationPolicies, setCancellationPolicies] = useState([])
    const [depositPolicies, setDepositPolicies] = useState([])

    useEffect(() => {
        if (controller) {
            window[controller] = async (action, params) => {
                switch (action) {
                    case "open":
                        initData(params.reservationId, params.reservation);
                        setOpen(true)
                        break;
                    default:
                        break;
                }
            }
        }
    }, [])

    useEffect(() => {
        setOpen(props.open)
    }, [props.open])

    useEffect(() => {
        initData(props.reservationId, props.reservation);
    }, [props.reservation, props.reservationId])

    useEffect(() => {
        if (policy?.CancellationPolicies) {
            setCancellationPolicies(policy.CancellationPolicies)
        }
        if (policy?.DepositPolicies) {
            setDepositPolicies(policy.DepositPolicies)
        }
    }, [policy])

    const initData = async (reservationId, reservation) => {
        loader(true)
        try {
            if (!reservation && reservationId) {
                reservation = await Reservations.GetReservation(reservationId)
            }
            if (!reservation) {
                return;
            }

            setReservation(reservation)

            const totals = await GetReservationTotals({
                ReservationIDs: [reservation.Id],
                HotelID: reservation.HotelId,
                TargetCurrencyId: currentHotel?.MainCurrencyId
            })
            setTotal(totals[0])

            if (reservation.ReservationPolicy?.CancellationPolicies?.length || reservation.ReservationPolicy?.DepositPolicies?.length) {
                setCancellationPolicies(reservation.ReservationPolicy?.CancellationPolicies || [])
                setDepositPolicies(reservation.ReservationPolicy?.DepositPolicies || [])
            }
            if (reservation.ReservationPolicyId) {
                const policies = await ReservationPolicy.GetReservationPolicies({
                    Id: reservation.ReservationPolicyId
                })
                if (policies?.length) {
                    setPolicy(policies[0])
                }
            } else if (reservation.ReservationPolicy) {
                setPolicy(reservation.ReservationPolicy)
            } 
        } catch (err) {
            onError(err);
        } finally {
            loader(false)
        }
    }

    const handleClosePopup = (e) => {
        e?.stopPropagation?.();
        e?.preventDefault?.();
        onClose?.()
        setOpen(false)
    }

    const hasGtdPolicy = useMemo(() => {
        return policy?.RequiredGtdType != null && policy.RequiredGtdType !== RequiredGtdTypeEnum.NoGuaranteeRequired;
    }, [policy])

    const gtdOverrideStatusName = useMemo(() => {
        if (!reservation?.GtdOverrideStatus) return null;
        const status = GtdOverrideStatuses.find(s => s.Id === reservation.GtdOverrideStatus);
        return status?.Name;
    }, [reservation?.GtdOverrideStatus])

    const hasCancellationOrDeposit = useMemo(() => {
        return total && (cancellationPolicies?.length > 0 || depositPolicies?.length > 0)
    }, [cancellationPolicies, depositPolicies, total])

    const hasPolicies = useMemo(() => {
        return hasCancellationOrDeposit || hasGtdPolicy
    }, [hasCancellationOrDeposit, hasGtdPolicy])

    const modalTitle = useMemo(() => {
        let title = text("ReservationPolicy")
        if (policy?.Name) {
            title += ` - ${policy.Name}`
        }
        return title;
    }, [policy])

    return (
        <Modal size="small" open={open} onClose={handleClosePopup} className='reservation-policy-popup' closeOnEscape closeOnDimmerClick>
             <Modal.Header>{modalTitle}</Modal.Header> 
            <Modal.Content>
                {hasPolicies
                    && (
                        <React.Fragment>
                            {total && cancellationPolicies?.length > 0 &&
                                <CancellationPoliciesList
                                    cancellationPolicies={cancellationPolicies}
                                    reservation={reservation}
                                    reservationTotal={total}
                                />
                            }
                            {total && depositPolicies?.length > 0 &&
                                <DepositPoliciesList
                                    depositPolicies={depositPolicies}
                                    reservation={reservation}
                                    reservationTotal={total}
                                />
                            }
                            {hasGtdPolicy && (
                                <Segment style={{ width: '100%' }}>
                                    <Label ribbon color='blue' style={{ minWidth: 120, textAlign: 'center' }}>
                                        {text("GuaranteePolicy")}
                                    </Label>
                                    <div style={{ marginTop: '10px' }}>
                                        <div style={{ marginBottom: '5px' }}>
                                            <span style={{ color: 'grey' }}>{text('RequiredGuaranteeType')}: </span>
                                            {text(policy.RequiredGtdType === RequiredGtdTypeEnum.CreditCardRequired ? 'CreditCardRequired' : 'RoomVoucherRequired')}
                                        </div>
                                        {policy.GtdUtcTime && (
                                            <div style={{ marginBottom: '5px' }}>
                                                <span style={{ color: 'grey' }}>{message('GuaranteeDescValidUntil')}: </span>
                                                {convertUtcTimeToHotelLocal(policy.GtdUtcTime, currentHotel?.HotelParameter?.TimeZone, reservation?.CheckInDate) || (typeof policy.GtdUtcTime === 'string' ? policy.GtdUtcTime.substring(0, 5) : policy.GtdUtcTime)}
                                            </div>
                                        )}
                                        {gtdOverrideStatusName && (
                                            <div style={{ marginBottom: '5px' }}>
                                                <span style={{ color: 'grey' }}>{text('GuaranteeOverrideStatus')}: </span>
                                                {text(gtdOverrideStatusName)}
                                            </div>
                                        )}
                                    </div>
                                </Segment>
                            )}
                        </React.Fragment>
                    )
                }

            </Modal.Content>
            <Modal.Actions style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button negative onClick={handleClosePopup}>
                    {text("Close")}
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

ReservationPolicyPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    reservation: PropTypes.object,
    reservationId: PropTypes.number,
    controller: PropTypes.any,
}

export default ReservationPolicyPopup